import type {Extension} from '@codemirror/state'
import {EditorView, type ViewUpdate} from '@codemirror/view'
import {debounce} from '@github/mini-throttle'

/** Data provided when a selection changes in CodeMirror */
export type SelectionChange = {
  view: EditorView
  from: number
  to: number
  text: string
  fromLine: number
  toLine: number
}

/** Configuration options for the selection listener extension */
export type SelectionListenerOptions = {
  onSelection?: (change: SelectionChange) => void
  onClear?: (view: EditorView) => void
  debounceMs?: number
}

/**
 * CodeMirror extension that notifies when a non-empty selection changes and when it is cleared.
 * - Calls onSelection with the current selection (from/to/text and line numbers).
 * - Calls onClear when the selection becomes empty after being non-empty.
 * - Debounced by default to avoid excessive calls while dragging.
 */
export function selectionListener({onSelection, onClear, debounceMs = 300}: SelectionListenerOptions = {}): Extension {
  let lastSignature = ''
  let emittingSelection = false

  const debouncedHandle = debounce((vu: ViewUpdate) => {
    const sel = vu.state.selection.main
    if (!emittingSelection || sel.empty) return

    const from = sel.from
    const to = sel.to
    const text = vu.state.doc.sliceString(from, to)

    // Signature to suppress identical consecutive events
    const signature = `${from}-${to}:${vu.state.doc.length}`
    if (lastSignature === signature) return

    lastSignature = signature

    const fromLine = vu.state.doc.lineAt(from).number
    const toLine = vu.state.doc.lineAt(to).number

    onSelection?.({view: vu.view, from, to, text, fromLine, toLine})
  }, debounceMs)

  return EditorView.updateListener.of(vu => {
    const sel = vu.state.selection.main
    if (sel.empty) {
      if (emittingSelection) {
        onClear?.(vu.view)
        lastSignature = ''
      }
      emittingSelection = false
      return
    }
    emittingSelection = true
    debouncedHandle(vu)
  })
}
